import React, { useState } from "react";
import UseContext from "hooks/useContext";
import SectionWrapper, {
    inputClassName,
} from "pages/createMission/components/SectionWrapper";
import Button from "components/button";
import { CrossIcon, Plus } from "assets/icons";
import {
    ADD_TASK_ERROR,
    TASK_LABEL_MAX_LENGTH,
    TASK_SOURCE,
    addCompanyTask,
    cleanTaskLabel,
    countSelectedTasks,
    removeCompanyTask,
    toggleProposedTask,
} from "pages/missionTemplates/utils/templateTasks";

const TasksTemplate = () => {
    const { templateData, setTemplateData, language } = UseContext();
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.tasks.${key}`, fallback);
    const [newTask, setNewTask] = useState("");
    const [addError, setAddError] = useState(null);

    const rows = templateData.tasks || [];
    const selectedCount = countSelectedTasks(rows);
    const hasProposals = rows.some(
        (row) => row.source === TASK_SOURCE.QUALIFICATION
    );
    const canAdd = cleanTaskLabel(newTask) !== "";

    const updateRows = (update) =>
        setTemplateData((prev) => ({ ...prev, tasks: update(prev.tasks || []) }));

    const handleAdd = () => {
        const result = addCompanyTask(rows, newTask);
        if (result.error) {
            setAddError(result.error === ADD_TASK_ERROR.EMPTY ? null : result.error);
            return;
        }
        updateRows(() => result.rows);
        setNewTask("");
        setAddError(null);
    };

    const handleKeyDown = (e) => {
        if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
        e.preventDefault();
        handleAdd();
    };

    const handleChange = (e) => {
        setNewTask(e.target.value);
        if (addError) setAddError(null);
    };

    const addPlaceholder = t(
        "addPlaceholder",
        "Ajouter une tâche (ex : accueil des livreurs)"
    );
    const removeLabel = t("removeLabel", "Retirer");
    const isPlural = selectedCount !== 1;
    const countText = (
        isPlural
            ? t("countMany", "{count} tâches")
            : t("countOne", "{count} tâche")
    ).replace("{count}", String(selectedCount));
    const selectedOfText = (
        isPlural
            ? t("selectedOfMany", "sélectionnées sur {total}")
            : t("selectedOfOne", "sélectionnée sur {total}")
    ).replace("{total}", String(rows.length));

    return (
        <SectionWrapper
            title={t("title", "Tâches du poste")}
            description={t(
                "description",
                "Cochez les tâches proposées ou ajoutez les vôtres."
            )}
        >
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                    <input
                        type="text"
                        name="newTask"
                        value={newTask}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        maxLength={TASK_LABEL_MAX_LENGTH}
                        placeholder={addPlaceholder}
                        aria-label={addPlaceholder}
                        aria-invalid={addError ? true : undefined}
                        className={`${inputClassName} flex-1 min-w-0 ${
                            addError ? "border-red-500" : ""
                        }`}
                    />
                    <Button
                        size={"SM"}
                        color={canAdd ? "PRIMARY" : "DISABLED"}
                        className={`h-10 shrink-0 ${
                            canAdd ? "" : "cursor-not-allowed"
                        }`}
                        onClick={handleAdd}
                        disabled={!canAdd}
                    >
                        <Plus wh={12} color={canAdd ? "#FFFFFF" : "#9CA3AF"} />
                        {t("addButton", "Ajouter")}
                    </Button>
                </div>
                {addError && (
                    <p className="ft-sm text-red-500" role="alert">
                        {addError === ADD_TASK_ERROR.DUPLICATE
                            ? t(
                                  "duplicate",
                                  "Cette tâche est déjà dans la liste."
                              )
                            : t(
                                  "tooMany",
                                  "Le nombre maximum de tâches est atteint."
                              )}
                    </p>
                )}
            </div>
            <p className="text-sm text-gray-500" aria-live="polite">
                <span className="font-medium text-gray-900">{countText}</span>{" "}
                {selectedOfText}
            </p>
            {!hasProposals && (
                <p className="text-sm text-gray-500 bg-gray-50 px-4 py-3">
                    {t(
                        "noProposal",
                        "Aucune tâche proposée pour cette qualification. Ajoutez celles de l'intérimaire."
                    )}
                </p>
            )}
            {rows.length > 0 && (
                <ul className="flex flex-col gap-2">
                    {rows.map((row) =>
                        row.source === TASK_SOURCE.COMPANY ? (
                            <li
                                key={row.key}
                                className="flex items-center justify-between gap-4 px-4 py-3 bg-white border-2 border-yellow-500"
                            >
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm text-gray-900 break-words">
                                        {row.label}
                                    </span>
                                    <span className="text-sm text-gray-400">
                                        {t("addedByYou", "Ajoutée par vous")}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        updateRows((prev) =>
                                            removeCompanyTask(prev, row.key)
                                        )
                                    }
                                    aria-label={`${removeLabel} ${row.label}`}
                                    className="shrink-0 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                >
                                    <CrossIcon wh={20} color="#111827" />
                                </button>
                            </li>
                        ) : (
                            <li key={row.key}>
                                <label
                                    className={`flex items-center gap-3 px-4 py-4 bg-white border-2 cursor-pointer hover:bg-gray-50 ${
                                        row.checked
                                            ? "border-yellow-500"
                                            : "border-gray-100"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!row.checked}
                                        onChange={() =>
                                            updateRows((prev) =>
                                                toggleProposedTask(prev, row.key)
                                            )
                                        }
                                        className="checkbox-yellow-sm"
                                    />
                                    <span className="min-w-0 text-sm text-gray-900 break-words">
                                        {row.label}
                                    </span>
                                </label>
                            </li>
                        )
                    )}
                </ul>
            )}
        </SectionWrapper>
    );
};

export default TasksTemplate;
